import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { buttonVariants } from 'v5/design-system/Button';
import { LoadingSpinner } from 'v5/design-system/LoadingSpinner';
import { CollectionDragHandle } from 'v5/patterns/dnd/CollectionDragHandle';
import { cn } from 'v5/platform/dom/cn';
import type { TileState } from './TileCollection';
import { TileBox } from './TileBox';
import { TileShell } from './TileShell';

type DataAttributes = { [name: `data-${string}`]: string | undefined };

/**
 * The media-focused body on the shared tile (`TileShell`): the picture is the tile, nothing on it
 * at rest; on hover or focus the name on a dark bar at the bottom, and the drag handle with the
 * host's actions (or Remove) top right. A moved tile slides to its place.
 */
export function ImmersiveCard({
  tile,
  media,
  name,
  caption,
  badge,
  actions,
  emphasized = false,
  removing = false,
  onRemove,
  ratio = 1,
  controlsAtRest = false,
  labels,
  ...data
}: {
  tile: TileState;
  /** What fills the tile (a thumbnail). */
  media: ReactNode;
  name: string;
  /** Under the name on the bar, e.g. tags. */
  caption?: ReactNode;
  /** In the corner the circle takes while picking. */
  badge?: ReactNode;
  /** The host's controls over the corner (a menu), in place of Remove. */
  actions?: ReactNode;
  /** Outlined, e.g. the one that stands for the others. */
  emphasized?: boolean;
  /** Waits with a spinner. */
  removing?: boolean;
  onRemove?: () => void;
  /** Its width over its height (`4 / 5` for a portrait, a picture's own); square without it. */
  ratio?: number;
  /** Remove and the drag handle show without a hover. */
  controlsAtRest?: boolean;
  /** `reorder`: the drag handle's name, for a tile that moves here or drags out. */
  labels: { select: string; reorder: string; remove: string };
} & DataAttributes) {
  return (
    <TileShell
      tile={{ ...tile, style: { ...tile.style, aspectRatio: ratio } }}
      selectLabel={labels.select}
      slides
      className="min-h-0 min-w-0 data-dragging:opacity-40"
      {...data}
    >
      {({ isSelected }) => (
        <>
          <TileBox
            // The row (React Aria) drops `aria-busy`; its content carries it.
            aria-busy={removing}
            className={cn(
              'relative min-h-0 flex-1 bg-bg-subtle',
              emphasized && 'ring-2 ring-bg-emphasis ring-offset-2',
              removing && 'opacity-50'
            )}
          >
            {media}
            {/* one line where it fits: the name gives way first, the caption keeps the end */}
            <span className="pointer-events-none absolute inset-x-0 bottom-0 flex flex-wrap items-center gap-x-2 gap-y-1 bg-black/45 px-2 pt-1.5 pb-1.5 text-left text-xs font-medium text-white opacity-0 backdrop-blur-md transition-opacity [text-shadow:0_1px_2px_rgb(0_0_0/0.45)] group-hocus/grid-item:opacity-100">
              <span className="min-w-24 flex-1 truncate leading-6">{name}</span>
              {caption}
            </span>
          </TileBox>
          {badge && (
            <span
              className={cn(
                'pointer-events-none absolute top-1.5 left-1.5 flex transition-opacity',
                // The selection circle takes the corner when it shows.
                tile.selectable &&
                  (isSelected || tile.picking
                    ? 'opacity-0'
                    : 'group-hocus/grid-item:opacity-0')
              )}
            >
              {badge}
            </span>
          )}
          {removing && (
            <span className="absolute inset-0 flex items-center justify-center">
              <LoadingSpinner className="size-5" />
            </span>
          )}
          {/* the tile's own controls in one corner, opposite the selection circle */}
          <div className="absolute top-1.5 right-1.5 z-10 flex gap-1">
            {tile.draggable && (
              <CollectionDragHandle
                label={labels.reorder}
                className={cn(
                  buttonVariants({
                    variant: 'overlay',
                    size: 'xs',
                    layout: 'icon',
                    shape: 'rounded'
                  }),
                  'transition-opacity group-hocus/grid-item:opacity-100 [&_svg]:size-3.5',
                  !controlsAtRest && 'opacity-0'
                )}
              />
            )}
            {actions && !removing && (
              <span
                className={cn(
                  'flex transition-opacity group-hocus/grid-item:opacity-100',
                  !controlsAtRest && 'opacity-0'
                )}
              >
                {actions}
              </span>
            )}
            {!actions && onRemove && !removing && (
              <AriaButton
                onPress={onRemove}
                aria-label={labels.remove}
                className={cn(
                  buttonVariants({
                    variant: 'overlay',
                    size: 'xs',
                    layout: 'icon',
                    shape: 'rounded'
                  }),
                  'transition-opacity group-hocus/grid-item:opacity-100',
                  !controlsAtRest && 'opacity-0'
                )}
              >
                <X className="size-3.5" />
              </AriaButton>
            )}
          </div>
        </>
      )}
    </TileShell>
  );
}
